import React, { useEffect, useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useOwnedSubmissions } from '../hooks/useOwnedSubmissions';
import { useCategoryNames } from '../hooks/useCategoryNames';
import { exportOwnedRawPhotos } from '../lib/callables';
import RawPhotoBrowse, { RawPhoto } from './RawPhotoBrowse';
import WhatYouGet from '../components/WhatYouGet';
import './MyLibrary.css';

type LibTab = 'rawPhoto' | 'annotatedPhoto' | 'rawVideo';

type MyLibraryProps = {
  user: User;
  sidebarOpen?: boolean;
};

export default function MyLibrary({ user, sidebarOpen = true }: MyLibraryProps) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { owned, loading } = useOwnedSubmissions(user.uid);
  const { resolve: resolveCategoryName, resolveLabelCategory, resolveLabelSubcategory } = useCategoryNames();

  const [libTab, setLibTab] = useState<LibTab>('rawPhoto');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({ category: 'all', subcategory: 'all', itemType: 'all', task: 'all' });
  const [exporting, setExporting] = useState(false);
  const [lastExport, setLastExport] = useState<{ url: string; count: number } | null>(null);

  // Open the requested type tab when arriving from Dashboard rows
  useEffect(() => {
    const raw = location.state?.libTab;
    if (raw !== 'rawPhoto' && raw !== 'annotatedPhoto' && raw !== 'rawVideo') return;
    setLibTab(raw);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const ownedFiltered = useMemo(
    () => owned.filter((o) => o.type === libTab),
    [owned, libTab]
  );

  // Map owned photos into the RawPhoto shape the tree expects, adding export status.
  const photos: RawPhoto[] = useMemo(
    () =>
      ownedFiltered.map((o) => ({
        id: o.submissionId,
        category: o.category,
        subcategory: o.subcategory,
        itemType: o.itemType,
        taskTitle: o.taskTitle,
        taskId: o.taskId,
        imageUrl: o.imageUrl,
        videoUrl: o.videoUrl,
        durationSeconds: o.durationSeconds,
        exported: (o.exportCount || 0) > 0 || !!o.lastExportedAt,
        lastExportedAt: o.lastExportedAt,
        labelClassIds: o.labelClassIds,
        labelCount: o.labelCount,
      })),
    [ownedFiltered]
  );

  const selectionSummary = useMemo(() => {
    const cats = new Set<string>();
    const subs = new Set<string>();
    const tasks = new Set<string>();
    for (const o of ownedFiltered) {
      if (!selected.has(o.submissionId)) continue;
      cats.add(o.category);
      subs.add(o.category + '||' + o.subcategory);
      tasks.add(o.category + '||' + o.subcategory + '||' + o.itemType + '||' + o.taskTitle);
    }
    return { categories: cats.size, subcategories: subs.size, tasks: tasks.size };
  }, [ownedFiltered, selected]);

  const selectedTotalLabels = useMemo(() => {
    let sum = 0;
    for (const o of ownedFiltered) {
      if (!selected.has(o.submissionId)) continue;
      sum += typeof o.labelCount === 'number' ? o.labelCount : 0;
    }
    return sum;
  }, [ownedFiltered, selected]);

  const toggleIds = (ids: string[], select: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };
  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const selectedCount = selected.size;

  const handleExport = async () => {
    if (selectedCount === 0) return;
    setExporting(true);
    try {
      const ids = Array.from(selected);
      const exportType =
        libTab === 'annotatedPhoto' ? 'annotatedPhoto' : libTab === 'rawVideo' ? 'rawVideo' : 'rawPhoto';
      const result = await exportOwnedRawPhotos(ids, exportType);
      setSelected(new Set());
      if (result.status === 'building') {
        setLastExport(null);
        alert(
          `Export started — ${result.photoCount} photo(s) are being prepared in the background. ` +
            `Open Exports History to watch progress and download when ready.`
        );
        navigate('/exports-history');
        return;
      }
      setLastExport({ url: result.downloadUrl, count: result.photoCount });
      alert(`Export ready — ${result.photoCount} photo(s). Click the Download button to save your ZIP.`);
    } catch (error: any) {
      console.error('Export failed:', error);
      alert(error?.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const switchTab = (tab: 'rawPhoto' | 'annotatedPhoto' | 'rawVideo') => {
    setLibTab(tab);
    setSelected(new Set());
  };

  const ownedCountLabel =
    libTab === 'rawVideo'
      ? `${ownedFiltered.length.toLocaleString()} raw video${ownedFiltered.length === 1 ? '' : 's'} owned`
      : libTab === 'annotatedPhoto'
        ? `${ownedFiltered.length.toLocaleString()} annotated photo${ownedFiltered.length === 1 ? '' : 's'} owned`
        : `${ownedFiltered.length.toLocaleString()} raw photo${ownedFiltered.length === 1 ? '' : 's'} owned`;
  const emptyTabLabel =
    libTab === 'rawVideo'
      ? 'No raw videos owned yet — switch tabs or browse the marketplace.'
      : libTab === 'annotatedPhoto'
        ? 'No annotated photos owned yet — switch tabs or browse the marketplace.'
        : 'No raw photos owned yet — switch tabs or browse the marketplace.';

  return (
    <div className="library-page" style={{ background: colors.background, color: colors.text, minHeight: '100vh', padding: 30 }}>
      <div className="library-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0' }}>My Library</h1>
          <p style={{ margin: 0, color: colors.secondary }}>
            {owned.length === 0
              ? 'Your purchased photos will appear here.'
              : ownedFiltered.length === 0
                ? emptyTabLabel
                : `${ownedCountLabel} — select any level and export.`}
          </p>
        </div>
        <button
          className="primary-btn"
          style={{ background: colors.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer' }}
          onClick={() => navigate('/marketplace')}
        >
          Browse Marketplace
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: colors.secondary }}>Loading your library…</div>
      ) : owned.length === 0 ? (
        <div className="empty-state" style={{ padding: 40, textAlign: 'center', color: colors.secondary }}>
          <p>You haven't purchased any photos yet.</p>
          <button
            style={{ background: colors.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/marketplace')}
          >
            Browse Marketplace
          </button>
        </div>
      ) : (
        <>
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            borderBottom: `2px solid ${colors.border}`,
            paddingBottom: '0',
          }}>
            <button
              onClick={() => switchTab('rawPhoto')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: libTab === 'rawPhoto' ? `3px solid ${colors.primary}` : '3px solid transparent',
                color: libTab === 'rawPhoto' ? colors.primary : colors.secondary,
                cursor: 'pointer',
                fontWeight: libTab === 'rawPhoto' ? '600' : '400',
                fontSize: '16px',
                transition: 'all 0.2s',
              }}
            >
              Raw Photos
            </button>
            <button
              onClick={() => switchTab('annotatedPhoto')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: libTab === 'annotatedPhoto' ? `3px solid ${colors.primary}` : '3px solid transparent',
                color: libTab === 'annotatedPhoto' ? colors.primary : colors.secondary,
                cursor: 'pointer',
                fontWeight: libTab === 'annotatedPhoto' ? '600' : '400',
                fontSize: '16px',
                transition: 'all 0.2s',
              }}
            >
              Annotated Photos
            </button>
            <button
              onClick={() => switchTab('rawVideo')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: libTab === 'rawVideo' ? `3px solid ${colors.primary}` : '3px solid transparent',
                color: libTab === 'rawVideo' ? colors.primary : colors.secondary,
                cursor: 'pointer',
                fontWeight: libTab === 'rawVideo' ? '600' : '400',
                fontSize: '16px',
                transition: 'all 0.2s',
              }}
            >
              Raw Video
            </button>
          </div>

          <WhatYouGet tier={libTab as 'rawPhoto' | 'annotatedPhoto' | 'rawVideo'} />

          <RawPhotoBrowse
            mode="library"
            photos={photos}
            loading={false}
            selected={selected}
            onToggleIds={toggleIds}
            filters={filters}
            onFilterChange={setFilters}
            expanded={expanded}
            onToggleExpand={toggleExpand}
            unitLabel={libTab === 'rawVideo' ? 'video' : 'photo'}
            showDuration={libTab === 'rawVideo'}
            labelNameResolver={libTab === 'annotatedPhoto' ? resolveCategoryName : undefined}
            resolveLabelCategory={libTab === 'annotatedPhoto' ? resolveLabelCategory : undefined}
            resolveLabelSubcategory={libTab === 'annotatedPhoto' ? resolveLabelSubcategory : undefined}
          />

          <div
            className="sticky-footer"
            style={{
              position: 'fixed',
              bottom: 0,
              left: sidebarOpen ? 220 : 72,
              right: 0,
              background: colors.card,
              borderTop: `1px solid ${colors.border}`,
              padding: '16px 30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'left 0.3s ease',
              zIndex: 100,
            }}
          >
            <div style={{ display: 'flex', gap: 36 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.secondary }}>Categories</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{selectionSummary.categories.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.secondary }}>Subcategories</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{selectionSummary.subcategories.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.secondary }}>Total Tasks</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{selectionSummary.tasks.toLocaleString()}</span>
              </div>
              {libTab === 'annotatedPhoto' && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.secondary }}>Total Labels</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{selectedTotalLabels.toLocaleString()}</span>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.secondary }}>
                  {libTab === 'rawVideo' ? 'Videos Selected' : 'Photos Selected'}
                </span>
                <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{selectedCount.toLocaleString()}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {lastExport && (
                <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 12 }}>
                  <a
                    href={lastExport.url}
                    download
                    style={{
                      background: colors.card,
                      color: colors.primary,
                      border: `1px solid ${colors.primary}`,
                      borderRadius: 8,
                      padding: '12px 24px',
                      fontSize: 16,
                      fontWeight: 600,
                      textDecoration: 'none',
                    }}
                  >
                    ⬇ Download ({lastExport.count})
                  </a>
                  <button
                    type="button"
                    aria-label="Dismiss download"
                    onClick={() => setLastExport(null)}
                    style={{
                      marginLeft: 6,
                      background: 'transparent',
                      border: `1px solid ${colors.border}`,
                      borderRadius: 6,
                      color: colors.secondary,
                      cursor: 'pointer',
                      fontSize: 14,
                      lineHeight: 1,
                      padding: '6px 8px',
                    }}
                  >
                    ✕
                  </button>
                </span>
              )}
              <button
                onClick={handleExport}
                disabled={selectedCount === 0 || exporting}
                style={{
                  background: selectedCount === 0 || exporting ? colors.border : colors.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: selectedCount === 0 || exporting ? 'not-allowed' : 'pointer',
                  opacity: selectedCount === 0 || exporting ? 0.6 : 1,
                }}
              >
                {exporting ? 'Exporting…' : 'Export Selected'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
